<template>
    <footer class="relative z-40 border-t border-black-text bg-tile-gray">
        <div
            class="mx-auto flex flex-col gap-6 px-4 py-8 md:px-0 lg:max-w-6xl 2xl:max-w-7xl"
        >
            <div class="font-promedium text-lg">
                {{ $t("kontakty.subtitle") }}
            </div>
            <div
                class="grid grid-cols-1 divide-black-text font-proregular text-sm md:grid-cols-3 md:divide-x"
            >
                <div class="flex flex-col gap-6 py-4 md:pr-6">
                    <nav class="flex flex-wrap items-center">
                        <NuxtLink
                            v-for="item in store.pages"
                            :key="item.to"
                            :to="localePath(item.to)"
                            class="pr-2 underline underline-offset-2 after:pl-2 after:content-['|'] last:after:content-none hover:no-underline"
                            no-prefetch
                        >
                            {{ useLocaleValue(item, "title") }}
                        </NuxtLink>
                    </nav>
                    <div class="flex flex-col gap-1">
                        <NuxtLink
                            :to="localePath('/mapy/atlas')"
                            class="group"
                            no-prefetch
                        >
                            <i class="pi pi-arrow-right text-xs" />
                            <span
                                class="pl-1 underline underline-offset-2 group-hover:no-underline"
                                >{{ $t("search.results_atlas") }}</span
                            >
                        </NuxtLink>
                        <NuxtLink
                            :to="
                                localePath('/cisla/katalog-indikatoru-a-metrik')
                            "
                            class="group"
                            no-prefetch
                        >
                            <i class="pi pi-arrow-right text-xs" />
                            <span
                                class="pl-1 underline underline-offset-2 group-hover:no-underline"
                                >{{ $t("kim.title") }}</span
                            >
                        </NuxtLink>
                        <NuxtLink
                            :to="localePath('/cisla/katalog-mestskych-casti')"
                            class="group"
                            no-prefetch
                        >
                            <i class="pi pi-arrow-right text-xs" />
                            <span
                                class="pl-1 underline underline-offset-2 group-hover:no-underline"
                                >{{ $t("kmec.title") }}</span
                            >
                        </NuxtLink>
                        <NuxtLink
                            :to="localePath('/cisla/pov')"
                            class="group"
                            no-prefetch
                        >
                            <i class="pi pi-arrow-right text-xs" />
                            <span
                                class="pl-1 underline underline-offset-2 group-hover:no-underline"
                                >{{
                                    useLocaleValue(
                                        store.getPageInfo("cisla-pov"),
                                        "title"
                                    )
                                }}</span
                            >
                        </NuxtLink>
                    </div>
                </div>
                <div class="flex flex-col gap-6 py-4 md:px-6">
                    <NuxtLink
                        to="mailto:uap@ipr.praha.eu"
                        external
                        target="_blank"
                        class="underline underline-offset-2 hover:no-underline"
                        >uap@ipr.praha.eu</NuxtLink
                    >
                    <div class="flex flex-col gap-1">
                        <NuxtLink
                            :to="
                                localePath(
                                    '/o-portalu/o-uzemne-analytickych-podkladech?tema=autorsky-tym'
                                )
                            "
                            class="underline underline-offset-2 hover:no-underline"
                            no-prefetch
                            >{{ $t("kontakty.autori") }}</NuxtLink
                        >
                        <NuxtLink
                            v-if="locale === 'cs'"
                            :to="localePath('/poskytovatele')"
                            class="underline underline-offset-2 hover:no-underline"
                            no-prefetch
                            >Návod pro poskytovatele</NuxtLink
                        >
                    </div>
                    <div class="flex flex-col items-start justify-start gap-1">
                        <button
                            class="underline underline-offset-2 hover:no-underline"
                            @click="store.cookiesSettingsModalActive = true"
                        >
                            {{ $t("general.cookies") }}
                        </button>
                        <NuxtLink
                            :to="
                                localePath(
                                    'https://app.iprpraha.cz/apl/mnt/App_UAP/portal/docs/Prohlaseni-Pristupnost.pdf'
                                )
                            "
                            target="_blank"
                            class="underline underline-offset-2 hover:no-underline"
                            no-prefetch
                        >
                            {{ $t("general.pristupnost") }}
                        </NuxtLink>
                    </div>
                </div>
                <div class="flex flex-col gap-6 py-4 md:pl-6">
                    <div class="flex flex-col gap-1">
                        <span class="font-promedium">
                            {{ $t("kontakty.ipr") }}
                        </span>
                        <div class="flex flex-wrap">
                            <span
                                class="whitespace-nowrap pr-2 after:pl-2 after:content-['|']"
                                >Vyšehradská 57, 128 00 Praha 2</span
                            >
                            <NuxtLink
                                to="https://iprpraha.cz"
                                target="_blank"
                                class="underline underline-offset-2 hover:no-underline"
                                >iprpraha.cz</NuxtLink
                            >
                        </div>
                    </div>
                    <div class="flex gap-4">
                        <NuxtLink
                            to="https://www.facebook.com/iprpraha"
                            external
                            target="_blank"
                            class="hover:text-hover-link"
                            title="Facebook IPR Praha"
                            alt="Facebook IPR Praha"
                        >
                            <i class="pi pi-facebook text-xl" />
                        </NuxtLink>
                        <NuxtLink
                            to="https://www.instagram.com/iprpraha"
                            external
                            target="_blank"
                            class="hover:text-hover-link"
                            title="Instagram IPR Praha"
                            alt="Instagram IPR Praha"
                        >
                            <i class="pi pi-instagram text-xl" />
                        </NuxtLink>
                        <NuxtLink
                            to="https://x.com/iprpraha"
                            external
                            target="_blank"
                            class="hover:text-hover-link"
                            title="X IPR Praha"
                            alt="X IPR Praha"
                        >
                            <i class="pi pi-twitter text-xl" />
                        </NuxtLink>
                    </div>
                    <div class="flex flex-wrap gap-10">
                        <NuxtLink to="/">
                            <img
                                class="h-10"
                                :src="`/img/logo-portal${locale === 'en' ? '-en' : '-cs'}-clean.webp`"
                                alt="Logo IPR Praha"
                            />
                        </NuxtLink>
                        <NuxtLink
                            to="https://iprpraha.cz"
                            external
                            target="_blank"
                            aria-label="Otevřít web IPR Praha"
                        >
                            <img
                                class="h-10"
                                src="/img/logo_ipr_praha_2025.png"
                                alt="Logo IPR Praha"
                            />
                        </NuxtLink>
                    </div>
                </div>
            </div>
            <ClientOnly>
                <div class="font-promedium text-sm">
                    © {{ $t("kontakty.ipr") }} {{ new Date().getFullYear() }},
                    {{ $t("kontakty.right_reserved") }}
                </div>
            </ClientOnly>
        </div>
    </footer>
</template>

<script lang="ts" setup>
const store = useMainStore()
const localePath = useLocalePath()

const { locale } = useI18n()
</script>

<style></style>
